import { Injectable, NgZone } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import {
  ChatRequest, ChatSession, ChatSessionsResponse, ChatSessionDetail,
  ControllersResponse, FeedbackRequest, FeedbackResponse, SSEEvent,
  MeResponse, UserUsageResponse, AdminVerifyResponse, ConfluenceValidateResponse,
  ExportCollectionRequest, ExportResponse,
  RenderFromCollectionRequest,
  SSEEventType,
} from '../models/chat.models';
import { ChatConfigService } from './chat-config.service';

@Injectable()
export class ChatApiService {

  constructor(
    private http: HttpClient,
    private config: ChatConfigService,
    private zone: NgZone
  ) {}

  private get base(): string { return this.config.backendUrl; }

  streamChat(req: ChatRequest): Observable<SSEEvent> {
    const subject = new Subject<SSEEvent>();
    const headers = { ...this.config.buildHeaders(), Accept: 'text/event-stream' };

    this.zone.runOutsideAngular(() => {
      fetch(`${this.base}/api/chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify(req),
      }).then(async (response) => {
        if (!response.ok) {
          const text = await response.text().catch(() => '');
          this.zone.run(() => subject.error(new Error(`HTTP ${response.status}: ${text}`)));
          return;
        }
        if (!response.body) {
          this.zone.run(() => subject.error(new Error('No response body')));
          return;
        }

        const reader  = response.body.getReader();
        const decoder = new TextDecoder();
        let   buffer  = '';
        let   currentEventType = '';  // tracks the most recent `event:` line

        const pump = async (): Promise<void> => {
          const { done, value } = await reader.read();
          if (done) { this.zone.run(() => subject.complete()); return; }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            // The SSE spec sends type on a separate `event:` line before `data:`
            if (line.startsWith('event: ')) {
              currentEventType = line.slice(7).trim();
              continue;
            }
            if (!line.startsWith('data: ')) {
              if (!line.trim()) currentEventType = ''; // blank line = message boundary
              continue;
            }
            const raw = line.slice(6).trim();
            if (!raw || raw === '[DONE]') continue;
            try {
              const data: any = JSON.parse(raw);

              // Normalise field names: the API uses different field names per event type
              //   tool_use:    { id, name, input }
              //   tool_result: { id, name, text, is_error, hidden }
              //   delta:       { text }
              //   done:        { final_text }
              const evt: SSEEvent = {
                // spread first so explicit keys below override
                ...data,
                type:        (data.type || currentEventType || 'unknown') as SSEEventType,
                text:        data.text,
                tool_id:     data.id    || data.tool_id,
                tool_name:   data.name  || data.tool_name,
                tool_input:  data.input || data.tool_input,
                tool_result: data.text  || data.tool_result,
                status:      data.message || data.status,
                stop_reason: data.stop_reason,
                usage:       data.usage,
                error:       data.error,
                model:       data.model,
              };

              this.zone.run(() => subject.next(evt));
              if (evt.type === 'done' || evt.type === 'error') {
                this.zone.run(() => subject.complete());
                return;
              }
            } catch { /* skip malformed */ }
            currentEventType = '';
          }
          return pump();
        };

        pump().catch(err => this.zone.run(() => subject.error(err)));
      }).catch(err => this.zone.run(() => subject.error(err)));
    });

    return subject.asObservable();
  }

  getSessions(): Observable<ChatSessionsResponse> {
    return this.http.get<ChatSessionsResponse>(
      `${this.base}/api/chat/sessions`,
      { headers: new HttpHeaders(this.config.buildGetHeaders()) }
    );
  }

  getSession(id: string): Observable<ChatSessionDetail> {
    return this.http.get<ChatSessionDetail>(
      `${this.base}/api/chat/sessions/${id}`,
      { headers: new HttpHeaders(this.config.buildGetHeaders()) }
    );
  }

  createSession(title?: string): Observable<ChatSession> {
    return this.http.post<ChatSession>(
      `${this.base}/api/chat/sessions`,
      { title: title || 'New conversation' },
      { headers: new HttpHeaders(this.config.buildHeaders()) }
    );
  }

  updateSession(id: string, title: string): Observable<ChatSession> {
    // Correct endpoint is /api/chat/sessions/{id}/title
    return this.http.patch<ChatSession>(
      `${this.base}/api/chat/sessions/${id}/title`,
      { title },
      { headers: new HttpHeaders(this.config.buildHeaders()) }
    );
  }

  /**
   * Persist the full message list for a session.
   * Called after every stream turn so coming back to the session reloads correctly.
   * Payload shape: { messages: [{role, text, tools:[]}], title?, model? }
   */
  saveSessionMessages(
    id: string,
    messages: Array<{ role: string; text: string; tools: any[] }>,
    title?: string,
    model?: string,
  ): Observable<any> {
    const body: any = { messages };
    if (title) body.title = title;
    if (model) body.model = model;
    return this.http.put(
      `${this.base}/api/chat/sessions/${id}`,
      body,
      { headers: new HttpHeaders(this.config.buildHeaders()) }
    );
  }

  deleteSession(id: string): Observable<void> {
    return this.http.delete<void>(
      `${this.base}/api/chat/sessions/${id}`,
      { headers: new HttpHeaders(this.config.buildGetHeaders()) }
    );
  }

  /**
   * Compact the session: folds the full transcript into a single summary,
   * freeing context tokens. POST /api/chat/sessions/{id}/compact
   */
  compactSession(id: string, model = 'claude-haiku-4-5-20251001'): Observable<any> {
    return this.http.post<any>(
      `${this.base}/api/chat/sessions/${id}/compact`,
      { model },
      { headers: new HttpHeaders(this.config.buildHeaders()) }
    );
  }

  /**
   * Zero the token meter for a session without touching messages.
   * POST /api/chat/sessions/{id}/usage/reset
   */
  resetSessionUsage(id: string): Observable<any> {
    return this.http.post<any>(
      `${this.base}/api/chat/sessions/${id}/usage/reset`,
      {},
      { headers: new HttpHeaders(this.config.buildHeaders()) }
    );
  }

  /**
   * Render a chart from structured data and return a downloadable PNG/SVG/PDF/CSV/JSON.
   * POST /api/charts/render
   */
  renderChart(payload: {
    type: string;
    title?: string;
    data: any;
    x_label?: string;
    y_label?: string;
    options?: Record<string, any>;
    format: 'png' | 'svg' | 'pdf' | 'csv' | 'json';
  }): Observable<{ id: string; filename: string; url: string; bytes: number; format: string }> {
    return this.http.post<any>(
      `${this.base}/api/charts/render`,
      payload,
      { headers: new HttpHeaders(this.config.buildHeaders()) }
    );
  }

  submitFeedback(req: FeedbackRequest): Observable<FeedbackResponse> {
    return this.http.post<FeedbackResponse>(
      `${this.base}/api/feedback`,
      req,
      { headers: new HttpHeaders(this.config.buildHeaders()) }
    );
  }

  getControllers(): Observable<ControllersResponse> {
    return this.http.get<ControllersResponse>(`${this.base}/api/controllers`);
  }

  health(): Observable<{ ok: boolean }> {
    return this.http.get<{ ok: boolean }>(`${this.base}/api/health`);
  }

  // ── Identity ──────────────────────────────────────────────────────────────

  /**
   * Resolve the current user's kuid from the active token.
   * GET /api/me
   */
  getMe(): Observable<MeResponse> {
    return this.http.get<MeResponse>(
      `${this.base}/api/me`,
      { headers: new HttpHeaders(this.config.buildGetHeaders()) }
    );
  }

  // ── Anthropic usage ───────────────────────────────────────────────────────

  /**
   * Per-user token spend roll-up. Includes budget/remaining when
   * USER_TOKEN_BUDGET is set on the backend.
   * GET /api/anthropic/user-usage
   */
  getUserUsage(): Observable<UserUsageResponse> {
    return this.http.get<UserUsageResponse>(
      `${this.base}/api/anthropic/user-usage`,
      { headers: new HttpHeaders(this.config.buildGetHeaders()) }
    );
  }

  // ── Admin ─────────────────────────────────────────────────────────────────

  /**
   * Confirm a user-supplied admin key matches ADMIN_KEY on the server.
   * POST /api/admin/verify
   */
  verifyAdminKey(adminKey: string): Observable<AdminVerifyResponse> {
    return this.http.post<AdminVerifyResponse>(
      `${this.base}/api/admin/verify`,
      {},
      { headers: new HttpHeaders({ 'X-Admin-Key': adminKey }) }
    );
  }

  /**
   * Self-test the Confluence integration (config, auth, search, pinned pages).
   * GET /api/admin/confluence/validate
   * Returns overall_ok:true only when all four checks pass.
   */
  validateConfluence(adminKey: string): Observable<ConfluenceValidateResponse> {
    return this.http.get<ConfluenceValidateResponse>(
      `${this.base}/api/admin/confluence/validate`,
      { headers: new HttpHeaders({ 'X-Admin-Key': adminKey }) }
    );
  }

  // ── Charts — render from collection ──────────────────────────────────────

  /**
   * Pull documents from a Kuzzle collection, aggregate, and render a chart.
   * POST /api/charts/render-from-collection
   * Use renderChart() when you already have inline data; use this when you
   * want the backend to fetch and aggregate from a Kuzzle collection directly.
   */
  renderChartFromCollection(payload: RenderFromCollectionRequest): Observable<{
    id: string; filename: string; url: string; bytes: number; format: string;
  }> {
    return this.http.post<any>(
      `${this.base}/api/charts/render-from-collection`,
      payload,
      { headers: new HttpHeaders(this.config.buildHeaders()) }
    );
  }

  // ── Server-side exports (from Kuzzle collections) ─────────────────────────

  /**
   * Export documents from a Kuzzle collection as CSV.
   * POST /api/export/csv
   * Returns { id, filename, url, bytes, rows, format }.
   * Download the file with downloadExport(filename).
   */
  exportCsv(req: ExportCollectionRequest): Observable<ExportResponse> {
    return this.http.post<ExportResponse>(
      `${this.base}/api/export/csv`,
      req,
      { headers: new HttpHeaders(this.config.buildHeaders()) }
    );
  }

  /**
   * Export documents from a Kuzzle collection as PDF.
   * POST /api/export/pdf
   */
  exportPdf(req: ExportCollectionRequest): Observable<ExportResponse> {
    return this.http.post<ExportResponse>(
      `${this.base}/api/export/pdf`,
      req,
      { headers: new HttpHeaders(this.config.buildHeaders()) }
    );
  }

  /**
   * Export a chart rendered from a Kuzzle collection as PNG.
   * POST /api/export/png
   */
  exportPng(req: ExportCollectionRequest): Observable<ExportResponse> {
    return this.http.post<ExportResponse>(
      `${this.base}/api/export/png`,
      req,
      { headers: new HttpHeaders(this.config.buildHeaders()) }
    );
  }

  /**
   * Download a previously exported file by filename.
   * GET /api/exports/{filename}
   * Returns the raw file blob — use with a Blob URL for browser download.
   */
  downloadExport(filename: string): Observable<Blob> {
    return this.http.get(
      `${this.base}/api/exports/${encodeURIComponent(filename)}`,
      { responseType: 'blob' }
    );
  }
}